
import React from 'react';
import { analyticsConfigured } from '../../services/attribution';
import { openCookieSettings } from '../ui/CookieConsent';
import { SiteConfig, FooterConfig, CompanySettings } from '../../types';
import { BrandLogo } from '../ui/BrandLogo';
import { Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Youtube, Music2, Globe, Building } from 'lucide-react';

interface SiteFooterProps {
    config: SiteConfig;
    company: CompanySettings;
    onAction: (action: string) => void;
}

interface SocialIconProps {
    type: string;
    url: string;
}

// Helper to render social icon
const SocialIcon: React.FC<SocialIconProps> = ({ type, url }) => {
    let Icon = Globe;
    if (type === 'instagram') Icon = Instagram;
    if (type === 'facebook') Icon = Facebook;
    if (type === 'tiktok') Icon = Music2;
    if (type === 'linkedin') Icon = Linkedin;
    if (type === 'youtube') Icon = Youtube;

    return (
        <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-accent/80 hover:bg-white/10 hover:text-white transition-all group"
            title={type}
        >
            <Icon size={14} />
        </a>
    );
};

export const SiteFooter: React.FC<SiteFooterProps> = ({ config, company, onAction }) => {
    const year = new Date().getFullYear();
    const theme = config.theme;
    
    // Fallback if footer config is missing
    const footer: FooterConfig = config.footer || {
        contact: {}, socials: {}, columns: []
    };

    return (
        <footer className="bg-primary text-white border-t border-white/5 relative z-10 pt-12 pb-6 font-sans transition-colors duration-500">
            <div className="max-w-7xl mx-auto px-6">
                
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12 border-b border-white/5 pb-12">
                    
                    {/* BRANDING & CONTACT */}
                    <div className="md:col-span-4 space-y-6">
                        <div className="scale-90 origin-left opacity-90 inline-block">
                            <BrandLogo variant="light" layout="minimal" theme={theme} />
                        </div>
                        
                        {footer.tagline && (
                            <p className="text-accent/70 text-sm leading-relaxed max-w-sm">
                                {footer.tagline}
                            </p>
                        )}

                        {/* Centrally Managed Contact Info */}
                        <div className="space-y-3 pt-2">
                            {company.phone && (
                                <a href={`tel:${company.phone}`} className="flex items-center gap-3 text-sm text-accent hover:text-white transition-colors group">
                                    <div className="w-6 h-6 rounded bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors"><Phone size={12} /></div>
                                    <span>{company.phone}</span>
                                </a>
                            )}
                            {company.email && (
                                <a href={`mailto:${company.email}`} className="flex items-center gap-3 text-sm text-accent hover:text-white transition-colors group">
                                    <div className="w-6 h-6 rounded bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors"><Mail size={12} /></div>
                                    <span>{company.email}</span>
                                </a>
                            )}
                            {(company.street || company.city) && (
                                <div className="flex items-start gap-3 text-sm text-accent">
                                    <div className="w-6 h-6 rounded bg-white/5 flex items-center justify-center shrink-0 mt-0.5"><MapPin size={12} /></div>
                                    <span className="leading-snug">
                                        {company.street}{company.street && company.city && ', '}{company.zip} {company.city}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* DYNAMIC COLUMNS */}
                    <div className="md:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">
                        {footer.columns.map(col => (
                            <div key={col.id}>
                                <h4 className="text-xs font-bold uppercase tracking-widest text-white mb-4 opacity-60">{col.title}</h4>
                                <ul className="space-y-2.5">
                                    {col.links.map((link, idx) => (
                                        <li key={idx}>
                                            <a 
                                                href={link.url} 
                                                target={link.url.startsWith('http') ? '_blank' : undefined}
                                                rel={link.url.startsWith('http') ? 'noopener noreferrer' : undefined}
                                                className="text-sm text-accent/70 hover:text-white transition-colors hover:translate-x-1 inline-block transform duration-200"
                                            >
                                                {link.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* BOTTOM BAR */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] text-accent/40 font-bold uppercase tracking-wide">
                    <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-center md:text-left">
                        <p>{config.theme.footerText || `© ${year} ${company.name}`}</p>
                        
                        {company.orgNumber && (
                            <>
                                <span className="hidden md:inline text-accent/20">•</span>
                                <p>Org.nr: {company.orgNumber}</p>
                            </>
                        )}
                        
                        <span className="hidden md:inline text-accent/20">•</span>
                        <a href="/#/info/terms" className="hover:text-white transition-colors">Köpvillkor</a>
                        <span className="hidden md:inline text-accent/20">•</span>
                        <a href="/#/info/privacy" className="hover:text-white transition-colors">Integritetspolicy</a>
                        <span className="hidden md:inline text-accent/20">•</span>
                        <a href="/#/angra" className="hover:text-white transition-colors">Ångra köpet här</a>

                        {analyticsConfigured() && (
                            <>
                                <span className="hidden md:inline text-accent/20">•</span>
                                <button type="button" onClick={openCookieSettings} className="hover:text-white transition-colors">Cookieinställningar</button>
                            </>
                        )}
                        
                        <span className="hidden md:inline text-accent/20">•</span>
                        <p>Powered by CoffeeNova</p>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        {Object.entries(footer.socials).map(([key, url]) => {
                            if (!url) return null;
                            return <SocialIcon key={key} type={key} url={url} />;
                        })}
                    </div>
                </div>
            </div>
        </footer>
    );
};
